import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Button, Modal, Input, Form, Space, Typography, Tooltip, Select, message} from 'antd';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';
import ProductStatusFilter from 'app/shared/components/ProductStatusFilter';
import type { ProductStatusFilterValue } from 'app/shared/components/productStatusFilterUtils';
import { matchesProductStatusFilter } from 'app/shared/components/productStatusFilterUtils';
import ManagerSearchByName from 'app/shared/components/ManagerSearchByName';
import { PlusOutlined, QuestionCircleOutlined, LockOutlined } from '@ant-design/icons';
import { Port, Terminal } from 'app/shared/model/Port';
import { getPorts } from './selectors';
import './Page.css';
import { fetchPorts, savePort, saveTerminal } from './actions';
import TransferInstructions from './TransferInstructions';
import UnsavedChangesModal from 'app/shared/ui/UnsavedChangesModal';
import { PublishedEventType, StatusIcon, useSignalREvent } from 'app/helpers/signalRHelper';

const { Title } = Typography;

const portTypes = ['Airport', 'Train Station'];
const portTypeOptions = portTypes.map((type) => ({ value: type, label: type }));

const TerminalTable: React.FC<{ port: Port; onSaveTerminal: (port: Port, terminal: Terminal) => Promise<void> }> = ({ port, onSaveTerminal }) => {
  const dispatch = useDispatch();
  const [terminalModalVisible, setTerminalModalVisible] = useState(false);
  const [terminalForm] = Form.useForm();
  const [editingTerminal, setEditingTerminal] = useState<Terminal | null>(null);
  const nextTerminalNumber = React.useMemo(() => {
    if (!port.terminals?.length) return 1;
    return Math.max(...port.terminals.map(t => Number(t.number) || 0)) + 1;
  }, [port.terminals]);
  const nextTerminalOrder = React.useMemo(() => {
    if (!port.terminals?.length) return 1;
    return Math.max(...port.terminals.map(t => Number(t.order ?? t.number) || 0)) + 1;
  }, [port.terminals]);
  const sortedTerminals = React.useMemo(
    () =>
      [...(port.terminals ?? [])].sort((a, b) => {
        const aOrder = Number(a?.order ?? a?.number ?? Number.POSITIVE_INFINITY);
        const bOrder = Number(b?.order ?? b?.number ?? Number.POSITIVE_INFINITY);
        return aOrder - bOrder;
      }),
    [port.terminals],
  );

  const onAddTerminal = () => {
    setEditingTerminal(null);
    terminalForm.resetFields();
    terminalForm.setFieldsValue({ number: 0, order: nextTerminalOrder, active: true });
    setTerminalModalVisible(true);
  };

  const onEditTerminal = (terminal: Terminal) => {
    setEditingTerminal(terminal);
    terminalForm.setFieldsValue({
      number: terminal.number,
      name: terminal.name,
      active: terminal.active,
      order: terminal.order ?? terminal.number,
    });
    setTerminalModalVisible(true);
  };

  const onSubmitTerminal = async () => {
      const values = await terminalForm.validateFields();
      const isNewTerminal = !editingTerminal;
      const number = isNewTerminal ? 0 : Math.max(1, parseInt(String(values.number ?? editingTerminal?.number ?? 0), 10));
      const orderSource = values.order ?? (editingTerminal?.order ?? nextTerminalOrder);
      const order = Math.max(1, parseInt(String(orderSource), 10));
      if (!isNewTerminal && number > 0) {
        const duplicate = port.terminals.some(
          (t) => Number(t.number) === number && t.id !== editingTerminal.id
        );
        if (duplicate) {
          return message.error('Terminal number must be unique within the port.');
        }
        }
      const duplicateOrder = port.terminals.some(
        (t) => Number(t.order ?? t.number) === order && (!editingTerminal || t.id !== editingTerminal.id)
      );
      if (duplicateOrder && (!editingTerminal || Number(editingTerminal.order ?? editingTerminal.number) !== order)) {
        return message.error('Order number must be unique within the port.');
      }

      const term: Terminal = {
          id: isNewTerminal ? 0 : (port.id * 1000 + number),
          number,
          name: (values.name || '').trim(),
          active: !!values.active,
          order,
        };

      try {
        await onSaveTerminal(port, term);
        setTerminalModalVisible(false);
        message.success(editingTerminal ? 'Terminal updated' : 'Terminal added');
      } catch (e: any) {
        console.error(e);
        message.error(e?.message || 'Failed to save terminal');
      }
    };

  const terminalColumns = [
    {
      title: 'Terminal ID',
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: (
        <>
          Order Number&nbsp;
          <Tooltip title="To define the order in which it will show up in the drop down.">
            <QuestionCircleOutlined />
          </Tooltip>
        </>
      ),
      dataIndex: 'order',
      key: 'order',
      render: (_: number, record: Terminal) => record.order ?? record.number,
    },
    {
      title: 'Terminal Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, terminal: Terminal) =>
        port.isLocked ? (
          text
        ) : (
          <Button type="link" style={{ padding: 0 }} onClick={() => onEditTerminal(terminal)}>
            {text}
          </Button>
        ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) =>
        active ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>,
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <Tooltip title={port.isLocked ? 'Locked because this port is used by active offerings' : ''}>
          <Button type="dashed" icon={<PlusOutlined />} onClick={onAddTerminal} disabled={port.isLocked}>
            Add Terminal
          </Button>
        </Tooltip>
      </div>
      <Table<Terminal>
        rowKey="id"
        dataSource={sortedTerminals}
        columns={terminalColumns}
        pagination={false}
        rowClassName={(record) => (!record.active ? 'inactive-row' : '')}
      />

      <Modal
        title={`${editingTerminal ? 'Edit' : 'Add'} Terminal for ${port.name}`}
        open={terminalModalVisible}
        okButtonProps={{ disabled: port.isLocked }}
        onCancel={() => setTerminalModalVisible(false)}
        onOk={onSubmitTerminal}
      >
        <Form form={terminalForm} layout="vertical">
          <Form.Item name="number" hidden preserve>
            <Input type="number" />
          </Form.Item>
          <Form.Item
            name="order"
            label="Order Number"
          >
            <Input type="number" min={1} />
          </Form.Item>
          <Form.Item name="name" label="Terminal Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <ActiveStatusFormItem name="active" initialValue={true} />
        </Form>
      </Modal>
    </>
  );
};

const PortManagerPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const ports = useSelector(getPorts) as Port[];
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPort, setSelectedPort] = useState<Port>();
  const [filterName, setFilterName] = useState('');
  const [filterType, setFilterType] = useState<string | undefined>();
  const [activeStatusFilter, setActiveStatusFilter] = useState<ProductStatusFilterValue>('all');

  const refreshGridEvent = async () => {
    dispatch(fetchPorts());
  }

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent([PublishedEventType.TransferPortEventPublished, PublishedEventType.TransferPortInstructionEventPublished, PublishedEventType.TransferPortTerminalEventPublished], refreshGridEvent);

  useEffect(() => { dispatch(fetchPorts()); }, [dispatch]);

  const handleAddPort = () => {
    form.resetFields();
    setSelectedPort(undefined);
    setModalVisible(true);
  };

  const handleEditPort = (port: Port) => {
    setSelectedPort(port);
    form.setFieldsValue(port);
    setModalVisible(true);
  };

const [pendingModalAction, setPendingModalAction] = useState<'close' | null>(null);

const closePortModal = useCallback(() => {
  setModalVisible(false);
  setSelectedPort(undefined);
  form.resetFields();
  setPendingModalAction(null);
}, [form]);

const requestPortModalClose = useCallback(() => {
  setPendingModalAction('close');
}, []);

const handlePendingConfirm = useCallback(() => {
  if (pendingModalAction === 'close') {
    closePortModal();
  }
}, [pendingModalAction, closePortModal]);

const handlePendingStay = useCallback(() => {
  setPendingModalAction(null);
}, []);

const handleSubmitPort = async () => {
  const values = await form.validateFields();
  const duplicate = ports.some(
    (p) =>
      p.name.trim().toLowerCase() === values.name.trim().toLowerCase() &&
      (!selectedPort || p.id !== selectedPort.id)
  );
  if (duplicate) return message.error('Port name must be unique.');

  const portToSave: Port = {
    id: selectedPort?.id ?? 0,
    name: values.name.trim(),
    type: values.type,
    active: !!values.active,
    terminals: selectedPort?.terminals ?? [],
    transferInstructions: values.transferInstructions ?? [],
    isLocked: selectedPort?.isLocked ?? false,
    entityUpdate: selectedPort?.entityUpdate ?? false,
  };

  try {
    await dispatch(savePort(portToSave));
    message.success(selectedPort ? 'Port updated' : 'Port added');
    closePortModal();
  } catch (e: any) {
    console.error(e);
    message.error(e?.message || 'Failed to save port');
  }
};

  const handlePersistInstructionsAfterRemove = useCallback(async (list: Port['transferInstructions']) => {
    if (!selectedPort) return;
    const currentValues = form.getFieldsValue();
    const portToSave: Port = {
      id: selectedPort.id,
      name: (currentValues.name ?? selectedPort.name).trim(),
      type: currentValues.type ?? selectedPort.type,
      active: currentValues.active ?? selectedPort.active,
      terminals: selectedPort.terminals ?? [],
      transferInstructions: list ?? [],
      isLocked: selectedPort.isLocked ?? false,
      entityUpdate: selectedPort.entityUpdate ?? false,
    };
    try {
      await dispatch(savePort(portToSave));
      message.success('Instructions updated');
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || 'Failed to update instructions');
    }
  }, [form, dispatch, selectedPort]);

  const filteredData = ports
    .filter((p) => p.name.toLowerCase().includes(filterName.toLowerCase()))
    .filter((p) => (filterType ? p.type === filterType : true))
    .filter((p) => matchesProductStatusFilter(p.active, activeStatusFilter))
    .sort((a, b) => a.name.localeCompare(b.name));

  const columns = [
    {
      title: 'Port Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Port) => (
        <Space>
          {record.isLocked || record.entityUpdate ? (
            text
          ) : (
            <Button type="link" style={{ padding: 0 }} onClick={() => handleEditPort(record)}>
              {text}
            </Button>
          )}
          {record.isLocked && (
            <Tooltip title="Locked because this port is used by active offerings / date strategy">
              <LockOutlined />
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: 'Port Type', dataIndex: 'type', key: 'type' },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 180,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: 'Last Modified By',
      dataIndex: 'lastModifiedBy',
      key: 'lastModifiedBy',
      width: 180,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) =>
        active ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>,
    },
  ];

  return (
    <>
      <Title level={3} style={{ margin: 0, marginBottom: 16 }}>Transfer Ports Manager</Title>
      <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <ManagerSearchByName value={filterName} onChange={setFilterName} />
          <Select
            placeholder="Filter by Type"
            style={{ width: 160 }}
            allowClear
            value={filterType}
            onChange={(value) => setFilterType(value)}
            options={portTypeOptions}
          />
          <ProductStatusFilter
            value={activeStatusFilter}
            onChange={setActiveStatusFilter}
          />
        </Space>
        <Space>
          <StatusIcon status={status} lastEventReceived={lastEventReceived} unpublishedChanges={Array.isArray(filteredData) && filteredData.some(x => x?.entityUpdate === true)} onManualRefresh={refreshGridEvent}/>
          <Button icon={<PlusOutlined />} onClick={handleAddPort}>
            Add Port
          </Button>
        </Space>
      </Space>

      <Table<Port>
        rowKey="id"
        dataSource={filteredData}
        columns={columns}
         expandable={{
    expandedRowRender: (port) => (
      <TerminalTable
        port={port}
        onSaveTerminal={async (p, t) => {
          try {
            await dispatch(saveTerminal(p, t));
          } catch (e: any) {
            console.error(e);
            message.error(e?.message || 'Failed to save terminal');
          }
        }}
      />
    ),
  }}
        rowClassName={(record) => (!record.active ? 'inactive-row' : '')}
      />

      <Modal
        title={selectedPort ? 'Edit Port' : 'Add Port'}
        open={modalVisible}
        onCancel={requestPortModalClose}
        onOk={handleSubmitPort}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Port Name" rules={[{ required: true }]}>
            <Input disabled={selectedPort?.isLocked} />
          </Form.Item>
          <Form.Item name="type" label="Port Type" rules={[{ required: true }]}>
            <Select disabled={selectedPort?.isLocked} options={portTypeOptions} />
          </Form.Item>
          <Form.Item name="transferInstructions" valuePropName="value">
             <TransferInstructions onRemovePersist={handlePersistInstructionsAfterRemove} />
          </Form.Item>
          <ActiveStatusFormItem
            name="active"
            initialValue={true}
            disabled={selectedPort?.isLocked === true && !selectedPort?.active}
          />
        </Form>
      </Modal>
      <UnsavedChangesModal
        open={pendingModalAction !== null}
        onLeave={handlePendingConfirm}
        onStay={handlePendingStay}
      />
    </>
  );
};

export default PortManagerPage;